<template>
  <div
    class="dropdown"
    :class="{ open: showOptions, disabled }"
    tabindex="0"
    @focusin="focusIn"
    @focusout="focusOut"
    v-on-clickaway="closeDropdown"
  >
    <div
      class="text"
      :class="{
        placeholder: hasPlaceholder,
        'whitespace-no-wrap': hasPlaceholder
      }"
      @mousedown="onClick"
      v-if="!searchable"
    >
      <span :class="{ capitalize: !hasPlaceholder }">
        {{ selectedText }}
      </span>
    </div>
    <input
      ref="search"
      class="w-full"
      type="text"
      :placeholder="placeholder"
      @focusin="showOptions = true"
      v-model="computedSearchText"
      v-else
    />
    <ul class="options" v-show="showOptions">
      <template v-if="computedOptions && computedOptions.length">
        <li
          class="option capitalize"
          :class="{
            selected: isSelectedOption(option),
            hovered: i === fakeHoverIndex
          }"
          :key="`option_${i}`"
          @click.stop="selectOption(option)"
          v-for="(option, i) in computedOptions"
        >
          {{ option.label }}
          <div class="text-sm" v-if="option.subLabel && option.subLabel.length">
            {{ option.subLabel }}
          </div>
        </li>
      </template>
      <li class="empty-state" @click.stop v-else-if="!canAdd">
        {{ emptyStateText }}
      </li>
      <li class="option" @click="$emit('add')" v-if="canAdd">
        <i class="bdg-icon plus fa-fw text-red" />
        Add New
      </li>
    </ul>
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";
import UsesOptions from "../../elements/UsesOptions";

export default {
  name: "Dropdown",
  extends: UsesOptions,
  mixins: [clickaway],
  props: {
    resetValue: { type: String, default: null },
    placeholder: { type: String, default: "" },
    searchText: { type: String },
    emptyStateText: { type: String, default: "No options available." },
    searchable: { type: Boolean, default: false },
    canAdd: Boolean,
    disabled: Boolean
  },
  data() {
    return {
      isMounted: false,
      localSearchText: "",
      showOptions: false,
      fakeHoverIndex: -1
    };
  },
  computed: {
    hasPlaceholder() {
      return this.selectedOption == null;
    },
    computedSearchText: {
      get() {
        if (typeof this.searchText !== "undefined") {
          return this.searchText;
        }
        return this.localSearchText;
      },
      set(val) {
        this.localSearchText = val;
        this.$emit("search-input", val);
      }
    },
    searchTextMatchesSelection() {
      if (this.searchable) {
        return this.computedSearchText === this.selectedText;
      }
      return true;
    }
  },
  watch: {
    selectedOption(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.searchTextMatchSelected();
        this.showOptions = false;
      }
    },
    searchTextMatchesSelection(bool) {
      if (!bool) {
        this.selectedOption = null;
        this.showOptions = true;
      }
    }
  },
  created() {
    this.searchTextMatchSelected();
  },
  mounted() {
    // Set keyup listening
    document.addEventListener("keyup", this.onKeyUp);
  },
  methods: {
    searchTextMatchSelected() {
      if (this.searchable && this.selectedOption) {
        this.computedSearchText = this.selectedText;
      }
    },
    onClick() {
      if (this.$el === document.activeElement) {
        this.showOptions = !this.showOptions;
      } else {
        this.$el.focus();
      }
    },
    focusIn() {
      if (!this.disabled) {
        if (this.searchable) {
          this.$refs.search.focus();
        } else {
          this.showOptions = true;
        }
        this.$emit("focusin");
      }
    },
    focusOut() {
      this.showOptions = false;
      this.fakeHoverIndex = -1;
      this.$emit("blur");
    },
    onKeyUp(event) {
      if (
        this.showOptions &&
        (this.$el === document.activeElement ||
          this.$refs.search === document.activeElement)
      ) {
        event.preventDefault();

        // Up arrow key
        if (event.code === "ArrowUp") {
          if (this.fakeHoverIndex <= 0) {
            this.fakeHoverIndex = this.computedOptions.length - 1;
          } else {
            this.fakeHoverIndex--;
          }
        }
        // Down arrow key
        else if (event.code === "ArrowDown") {
          if (
            this.fakeHoverIndex < 0 ||
            this.fakeHoverIndex >= this.computedOptions.length - 1
          ) {
            this.fakeHoverIndex = 0;
          } else {
            this.fakeHoverIndex++;
          }
        }
        // Esc key
        else if (event.code === "Escape") {
          this.showOptions = false;
          this.fakeHoverIndex = -1;
        }
        // Enter key
        else if (event.code === "Enter") {
          if (this.fakeHoverIndex > -1) {
            this.selectOption(this.computedOptions[this.fakeHoverIndex]);
          }
          this.showOptions = false;
          this.fakeHoverIndex = -1;
        }
      }
    },
    closeDropdown() {
      this.showOptions = false;
    }
  }
};
</script>

<style lang="scss" scoped></style>
